import { ReactElement, useEffect, useState } from "react";
import { Route, Routes } from "react-router";
import { useFeatureIsOn, useGrowthBook } from "@growthbook/growthbook-react";
import { PROPELX, RUMI_LAUNCHPAD } from "@platform/feature-flags";
import { RouteConfig } from "@platform/types";
import { useUser } from "@platform/user/client";

import { ROUTE_NAMES } from "../constants";
import {
  Auth0Callback,
  CentroLogin,
  Dashboard,
  Login,
  Onboarding,
  OnboardingError,
  PageNotFound,
  ResetPassword,
} from "../pages";
import { newRoutesPerRole } from "../utils/router";
import { getAuth0Connection } from "../auth/auth0Pkce";

const ROUTE_ROOT = "/";

// Pre-auth pages keep the brand's light identity: their look is a fixed light
// background asset plus white fills, and the dark toggle is an in-app preference
// that shouldn't reach a screen the user hasn't logged in to yet. Setting
// `data-mui-color-scheme="light"` re-scopes MUI's light palette variables to this
// subtree, so the design-system components render light to match the backdrop
// instead of following the app's active scheme. `display: contents` keeps the
// wrapper out of layout.
const LightScheme = ({
  children,
}: {
  children: ReactElement;
}): ReactElement => (
  <div data-mui-color-scheme="light" style={{ display: "contents" }}>
    {children}
  </div>
);

const AppRoutes = (): ReactElement => {
  const enablePropelx = useFeatureIsOn(PROPELX);
  const enableRumiLaunchpad = useFeatureIsOn(RUMI_LAUNCHPAD);
  const { user } = useUser();
  const signedInUser = user.status === "signedIn" ? user : undefined;
  const isAuthenticated = signedInUser !== undefined;
  const gb = useGrowthBook();
  const gbReady = gb?.ready ?? false;
  const [isNotFound, setIsNotFound] = useState(false);
  const [auth0Connection, setAuth0Connection] = useState<string | null>(null);

  useEffect(() => {
    if (!gbReady) return;
    const timer = setTimeout(() => setIsNotFound(true), 100);
    return () => clearTimeout(timer);
  }, [gbReady]);

  useEffect(() => {
    // Check if Auth0 authentication is in progress
    // Only use stored connection if we're not already on a /login/:connection route
    const isOnLoginRoute = window.location.pathname.startsWith(
      `/${ROUTE_NAMES.LOGIN}/`,
    );
    if (!isOnLoginRoute) {
      const connection = getAuth0Connection();
      setAuth0Connection(connection);
    }
  }, []);

  const getRootElement = (): ReactElement => {
    // If Auth0 is authenticating, show CentroLogin
    if (auth0Connection) {
      return (
        <LightScheme>
          <CentroLogin connection={auth0Connection} />
        </LightScheme>
      );
    }
    // Otherwise show Dashboard or Login based on authentication status
    return isAuthenticated ? (
      <Dashboard />
    ) : (
      <LightScheme>
        <Login />
      </LightScheme>
    );
  };

  return (
    <Routes>
      <Route path={ROUTE_ROOT} element={getRootElement()} />
      <Route
        path={`/${ROUTE_NAMES.LOGIN}/:connection`}
        element={
          <LightScheme>
            <CentroLogin />
          </LightScheme>
        }
      />
      <Route path="/centro/callback" element={<Auth0Callback />} />
      <Route
        path={ROUTE_NAMES.RESET_PASSWORD}
        element={
          <LightScheme>
            <ResetPassword />
          </LightScheme>
        }
      />
      <Route
        path={ROUTE_NAMES.ONBOARDING_ERROR}
        element={
          <LightScheme>
            <OnboardingError />
          </LightScheme>
        }
      />
      {iterateRouteConfigs(
        newRoutesPerRole(
          signedInUser?.role,
          signedInUser?.isBrokerOrAgent ?? false,
          enablePropelx,
          enableRumiLaunchpad,
        ),
      )}
      <Route path={ROUTE_NAMES.ONBOARDING} element={<Onboarding />} />
      <Route path="*" element={isNotFound ? <PageNotFound /> : null} />
    </Routes>
  );
};

function iterateRouteConfigs(routeConfigs: RouteConfig[]): ReactElement[] {
  return routeConfigs.map((route) => (
    <Route key={route.path} path={route.path} element={route.element}>
      {route.children && iterateRouteConfigs(route.children)}
    </Route>
  ));
}

export default AppRoutes;
